import { isEqual } from 'lodash';

import { AsyncTrackQueue } from './AsyncTrackQueue';
import {
  type MediaStreamFactory,
  type SourceMediaTrackDiagnostics,
  TrackRegistry,
} from './TrackRegistry';

import { selectReceiverTrackByKind } from 'features/sip/helpers';
import type { RtcConnection } from 'features/sip/interfaces';
import { SourceMediaTypes } from 'features/source-media-tracks/enums';

export interface SourceMediaTrack {
  sourceMediaType: SourceMediaTypes;
  track: MediaStreamTrack;
}

export class SourceMediaTracks {
  public static StreamsChangedEventName = 'streamsChanged';

  private static instance: SourceMediaTracks;

  private readonly eventTarget: EventTarget;
  private readonly userData: Map<SourceMediaTypes, any>;
  private readonly requestCoordinator: AsyncTrackQueue<
    SourceMediaTypes,
    MediaStreamTrack
  >;
  private readonly trackRegistry: TrackRegistry;
  private videoSettings?: MediaTrackConstraints;

  private getUserMedia: (
    constraints?: MediaStreamConstraints
  ) => Promise<MediaStream>;

  private constructor() {
    this.eventTarget = new EventTarget();
    this.userData = new Map();
    this.requestCoordinator = new AsyncTrackQueue();

    this.getUserMedia = (constraints?: MediaStreamConstraints) => {
      if (
        this.videoSettings &&
        constraints?.video &&
        typeof constraints.video === 'object'
      ) {
        return navigator.mediaDevices.getUserMedia({
          ...constraints,
          video: {
            ...this.videoSettings,
            ...constraints.video,
          },
        });
      }

      return navigator.mediaDevices.getUserMedia(constraints);
    };

    this.trackRegistry = new TrackRegistry((constraints) =>
      this.getUserMedia(constraints)
    );
  }

  // Note: forceNewInstance should ONLY be used in testing scenarios
  public static getInstance(forceNewInstance = false): SourceMediaTracks {
    if (!SourceMediaTracks.instance || forceNewInstance) {
      SourceMediaTracks.instance = new SourceMediaTracks();
    }
    return SourceMediaTracks.instance;
  }

  public overrideDependencies(
    getUserMedia: (constraints?: MediaStreamConstraints) => Promise<MediaStream>
  ) {
    this.getUserMedia = getUserMedia;
  }

  public async getTrack(
    mediaType: SourceMediaTypes
  ): Promise<MediaStreamTrack> {
    const cachedTrack = this.trackRegistry.getTrack(mediaType);

    if (cachedTrack) {
      if (cachedTrack.readyState === 'ended') {
        console.log(
          'Debug: Detected ended track, clearing cache for mediaType:',
          mediaType
        );
        this.trackRegistry.clearTrack(mediaType);
        this.dispatchTracksChanged();
      } else {
        return cachedTrack;
      }
    }

    return this.requestCoordinator.run(mediaType, async (generation) => {
      const freshCachedTrack = this.trackRegistry.getTrack(mediaType);
      if (freshCachedTrack?.readyState === 'live') {
        return freshCachedTrack;
      }

      const entry = this.trackRegistry.getEntry(mediaType);
      const { track, monitoredTrackId } = await this.createMonitoredTrack(
        mediaType,
        entry.trackFactory
      );

      if (!this.requestCoordinator.isCurrentGeneration(mediaType, generation)) {
        console.log(
          `Discarding stale track for mediaType: ${mediaType}, trackId: ${track.id}`
        );
        track.stop();
        return this.getTrack(mediaType);
      }

      this.trackRegistry.setMonitoredTrackId(mediaType, monitoredTrackId);
      this.trackRegistry.setTrack(mediaType, track);
      this.dispatchTracksChanged();

      return track;
    });
  }

  /**
   * Forces a fresh local device track so the next call cannot reuse a cached
   * but unhealthy managed track from a previously failed session.
   */
  public async refreshTrack(
    mediaType: SourceMediaTypes
  ): Promise<MediaStreamTrack> {
    if (!TrackRegistry.isLocalMediaType(mediaType)) {
      throw new Error(
        `Cannot refresh non-local source media type: ${mediaType}`
      );
    }

    const cachedTrack = this.trackRegistry.getTrack(mediaType);

    this.trackRegistry.clearTrack(mediaType);

    if (cachedTrack?.readyState === 'live') {
      cachedTrack.stop();
    }
    this.dispatchTracksChanged();

    return this.getTrack(mediaType);
  }

  public isTrackAvailable(mediaType: SourceMediaTypes) {
    return this.trackRegistry.isTrackAvailable(mediaType);
  }

  public setDeafRtcConnection(rtcConnection: RtcConnection | undefined) {
    let audioTrack: MediaStreamTrack | undefined = undefined;
    let videoTrack: MediaStreamTrack | undefined = undefined;

    if (rtcConnection) {
      const { track: selectedAudioTrack } = selectReceiverTrackByKind(
        rtcConnection,
        'audio'
      );
      const { track: selectedVideoTrack } = selectReceiverTrackByKind(
        rtcConnection,
        'video'
      );

      audioTrack = selectedAudioTrack;
      videoTrack = selectedVideoTrack;
    }

    let tracksChanged = false;

    if (
      audioTrack?.id !==
      this.trackRegistry.getEntry(SourceMediaTypes.DEAF_AUDIO).deviceId
    ) {
      this.processRemoteTrackChanged(
        SourceMediaTypes.DEAF_AUDIO,
        audioTrack?.id,
        audioTrack,
        false
      );
      tracksChanged = true;
    }

    if (
      videoTrack?.id !==
      this.trackRegistry.getEntry(SourceMediaTypes.DEAF_VIDEO).deviceId
    ) {
      this.processRemoteTrackChanged(
        SourceMediaTypes.DEAF_VIDEO,
        videoTrack?.id,
        videoTrack,
        false
      );
      tracksChanged = true;
    }

    if (tracksChanged) {
      this.dispatchTracksChanged();
    }
  }

  public setRemoteTrack(
    mediaType: SourceMediaTypes,
    track: MediaStreamTrack | undefined
  ): void {
    if (TrackRegistry.isLocalMediaType(mediaType)) {
      throw new Error(
        `Cannot set remote stream for local mediaType: ${mediaType}`
      );
    }

    this.processRemoteTrackChanged(mediaType, undefined, track);
  }

  public setCameraId(cameraId: any) {
    if (
      cameraId ===
      this.trackRegistry.getEntry(SourceMediaTypes.INTERP_VIDEO).deviceId
    ) {
      return;
    }

    this.processLocalDeviceChanged(
      SourceMediaTypes.INTERP_VIDEO,
      cameraId ?? undefined,
      cameraId
        ? {
            video: {
              deviceId: { exact: cameraId },
            },
            audio: false,
          }
        : undefined
    );
  }

  public setMicrophoneId(microphoneId: any) {
    if (
      microphoneId ===
      this.trackRegistry.getEntry(SourceMediaTypes.INTERP_AUDIO).deviceId
    ) {
      return;
    }

    this.processLocalDeviceChanged(
      SourceMediaTypes.INTERP_AUDIO,
      microphoneId,
      {
        video: false,
        audio: {
          deviceId: microphoneId ? { exact: microphoneId } : undefined,
        },
      }
    );
  }

  public async getActiveTracks(): Promise<SourceMediaTrack[]> {
    return Object.entries(this.trackRegistry.getEntries())
      .filter(([, entry]) => entry.track)
      .map(([sourceMediaType, entry]) => ({
        sourceMediaType: sourceMediaType as SourceMediaTypes,
        track: entry.track!,
      }));
  }

  public addStreamsChangedListener(
    listener: EventListenerOrEventListenerObject
  ): void {
    this.eventTarget.addEventListener(
      SourceMediaTracks.StreamsChangedEventName,
      listener
    );
  }

  public removeStreamsChangedListener(
    listener: EventListenerOrEventListenerObject
  ): void {
    this.eventTarget.removeEventListener(
      SourceMediaTracks.StreamsChangedEventName,
      listener
    );
  }

  public getUserData(sourceMediaType: SourceMediaTypes): any {
    return this.userData.get(sourceMediaType);
  }

  public setUserData(sourceMediaType: SourceMediaTypes, userData: any): void {
    if (!userData) {
      this.userData.delete(sourceMediaType);
      return;
    }
    this.userData.set(sourceMediaType, userData);
  }

  public setVideoSettings(settings: MediaTrackConstraints): void {
    if (this.videoSettings && isEqual(this.videoSettings, settings)) {
      return;
    }
    this.videoSettings = settings;
  }

  public getTrackDiagnostics(): Record<
    SourceMediaTypes,
    SourceMediaTrackDiagnostics
  > {
    return this.trackRegistry.getDiagnostics();
  }

  private dispatchTracksChanged() {
    const detail = { streams: this.trackRegistry.getEntries() };
    const event = new CustomEvent(SourceMediaTracks.StreamsChangedEventName, {
      detail,
    });
    this.eventTarget.dispatchEvent(event);
  }

  private async createMonitoredTrack(
    mediaType: SourceMediaTypes,
    trackFactory: MediaStreamFactory
  ): Promise<{ track: MediaStreamTrack; monitoredTrackId: string }> {
    const monitoredTrackId = crypto.randomUUID();
    const track = await trackFactory();

    const checkClearTrack = () => {
      if (
        this.trackRegistry.getMonitoredTrackId(mediaType) === monitoredTrackId
      ) {
        this.trackRegistry.clearTrack(mediaType);
        this.dispatchTracksChanged();
      }
    };

    const originalStop = track.stop.bind(track);

    track.stop = () => {
      console.log(`Detected track stopped for mediaType: ${mediaType}`);
      originalStop();
      checkClearTrack();
    };

    track.onended = () => {
      console.log(`Detected track ended for mediaType: ${mediaType}`);
      checkClearTrack();
    };

    return {
      track,
      monitoredTrackId,
    };
  }

  private processRemoteTrackChanged(
    mediaType: SourceMediaTypes,
    deviceId: any,
    track: MediaStreamTrack | undefined,
    dispatchEvent = true
  ) {
    this.requestCoordinator.bumpGeneration(mediaType);
    this.trackRegistry.replaceRemoteTrack(mediaType, deviceId, track);

    if (dispatchEvent) {
      this.dispatchTracksChanged();
    }
  }

  private processLocalDeviceChanged(
    mediaType: SourceMediaTypes,
    deviceId: any,
    newConstraints: MediaStreamConstraints | undefined,
    dispatchEvent = true
  ) {
    this.requestCoordinator.bumpGeneration(mediaType);
    this.trackRegistry.replaceLocalDevice(mediaType, deviceId, newConstraints);

    if (dispatchEvent) {
      this.dispatchTracksChanged();
    }
  }
}
